import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {Box, Grid, Tooltip} from "@mui/material";
import {parseError} from "../../../../errors/http.service";
import {useSnackbar} from "../../../../contexts/SnackbarContext";
import {useUser} from "../../../../contexts/UserContext";
import {ActionButton} from "../../../../components/common/ActionButtons";
import {AvatarContainer, ButtonGroup, StyledAvatar} from "../../UserSettings.styled";
import {useTranslation} from 'react-i18next';
import AvatarCropDialog from "../../../../components/dialogs/AvatarCropDialog";
import FieldHelpSuffix from "../../../../components/common/FieldHelpSuffix";
import {ResetAvatarButton, StyledAvatarButton, StyledTextField} from "./UserData.styled";
import {normalizePhoneNumber, phoneInputProps, validatePhoneNumber} from "../../../../utils/phone-validation";
import {tokens} from "../../../../theme";
import {authService} from "../../../../services/auth.service";
import CloseIcon from "@mui/icons-material/Close";

const MAX_SIZE = 1024 * 1024;

const UserData= () => {
    const { t } = useTranslation();
    const {user, fetchUserData} = useUser();

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [position, setPosition] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    const [phoneNumberTouched, setPhoneNumberTouched] = useState<boolean>(false);

    const [avatar, setAvatar] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    const [isAvatarChanged, setIsAvatarChanged] = useState<boolean>(false);

    const [cropDialogOpen, setCropDialogOpen] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const {showSnackbar} = useSnackbar();
    const normalize = (v?: string | null) => (v ?? "").trim();

    const phoneNumberValidationError = validatePhoneNumber(phoneNumber);
    const showPhoneNumberError = phoneNumberTouched && !!phoneNumberValidationError;

    const isFormChanged = user ? (
        (firstName !== (user.firstName) && firstName !== "") ||
        (lastName !== (user.lastName) && lastName !== "") ||
        (normalize(position) !== normalize(user.position)) ||
        (normalize(phoneNumber) !== normalize(user.phoneNumber)) ||
        isAvatarChanged
    ) : false;

    useEffect(() => {
        if (!user) {
            void fetchUserData();
        } else {
            setFirstName(user.firstName ?? "");
            setLastName(user.lastName ?? "");
            setPosition(user.position ?? "");
            setPhoneNumber(user.phoneNumber ?? "");
        }
    }, [user, fetchUserData]);

    const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > MAX_SIZE) {
                showSnackbar(t('userData.fileTooLarge'), "error");
                setAvatar(null);
                event.target.value = "";
                return;
            }

            setSelectedFile(file);
            setCropDialogOpen(true);
        }
    };

    const handleCropSave = (croppedBlob: Blob) => {
        const croppedFile = new File([croppedBlob], selectedFile?.name || 'avatar.png', {
            type: 'image/png'
        });
        
        setAvatar(croppedFile);
        setAvatarPreview(URL.createObjectURL(croppedBlob));
        setIsAvatarChanged(true);
        
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleCropClose = () => {
        setCropDialogOpen(false);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleAvatarUpload = async (fileToUpload?: File) => {
        const file = fileToUpload || avatar;
        if (!file) return;

        try {
            await authService.uploadAvatar(file);

            showSnackbar(t('userData.avatarUpdated'), "success");
            await fetchUserData();
        } catch (error) {
            const parsedError = parseError(error);
            showSnackbar(parsedError, "error");
        }
    };

    const resetAvatar = () => {
        setAvatar(null);
        setAvatarPreview(null);
        setIsAvatarChanged(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSaveChanges = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setPhoneNumberTouched(true);

        if (phoneNumberValidationError) return;

        try {
            const profileData: { firstName?: string; lastName?: string; position?: string; phoneNumber?: string } = {};

            if ((firstName !== user?.firstName) && (firstName !== "")) {
                profileData.firstName = firstName;
            }

            if ((lastName !== user?.lastName) && (lastName !== "")) {
                profileData.lastName = lastName;
            }

            if (position !== user?.position) {
                profileData.position = position;
            }

            if (phoneNumber !== user?.phoneNumber && !phoneNumberValidationError) {
                profileData.phoneNumber = phoneNumber;
            }

            if (Object.keys(profileData).length > 0) {
                await authService.updateCurrentUserData(profileData);
                showSnackbar(t('userData.profileUpdated'), "success");
            }

            if (isAvatarChanged && avatar) {
                await handleAvatarUpload(avatar);
                setIsAvatarChanged(false);
            }

            await fetchUserData();
        } catch (error) {
            const parsedError = parseError(error);
            showSnackbar(parsedError, "error");
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setPhoneNumberTouched(false);
        }
    };

    if (!user) {
        return null;
    }

    const userFirstName = user.firstName || "";
    const userLastName = user.lastName || "";
    const userEmail = user.email || "";
    const userInitials = userFirstName && userLastName
        ? `${userFirstName.charAt(0)}${userLastName.charAt(0)}`
        : (userFirstName
            ? userFirstName.charAt(0)
            : (userEmail ? userEmail.charAt(0).toUpperCase() : '?'));

    return (
        <Box component="form"
             onSubmit={handleSaveChanges}
             sx={{
                 paddingTop: { xs: '24px', md: '48px' },
                 display: 'flex',
                 flexDirection: 'column',
                 height: '100%'
             }}
        >
            <Box sx={{flex: 1}}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <AvatarContainer style={{position: 'relative'}}>
                            {avatarPreview || user.avatarBase64 ? (
                                <StyledAvatar
                                    src={avatarPreview || user.avatarBase64}
                                    alt={userFirstName || t('userData.userAvatar')}
                                    style={{border: '1px solid #e4e4e4'}}
                                />
                            ) : (
                                <StyledAvatar
                                    sx={{
                                        backgroundColor: tokens.colors.blue500,
                                        fontSize: '3rem',
                                        fontWeight: 'bold',
                                        width: '150px',
                                        height: '150px'
                                    }}
                                    style={{border: '1px solid #e4e4e4'}}
                                >
                                    {userInitials}
                                </StyledAvatar>
                            )}

                            {isAvatarChanged && (
                                <Tooltip title={t('userData.resetAvatar')}>
                                    <ResetAvatarButton onClick={resetAvatar}>
                                        <CloseIcon />
                                    </ResetAvatarButton>
                                </Tooltip>
                            )}

                            <input
                                type="file"
                                accept="image/*"
                                style={{display: "none"}}
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                            />
                            <StyledAvatarButton
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t('userData.changeProfilePhoto')}
                            </StyledAvatarButton>
                        </AvatarContainer>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.username')}
                                value={userEmail}
                                disabled
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.username')}
                                            ariaLabel={t('userData.help.username') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.firstName')}
                                value={firstName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.firstName')}
                                            ariaLabel={t('userData.help.firstName') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.lastName')}
                                value={lastName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.lastName')}
                                            ariaLabel={t('userData.help.lastName') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.position')}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.position')}
                                            ariaLabel={t('userData.help.position') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>

                        <Box mb={3}>
                            <StyledTextField
                                label={t('userData.phoneNumber')}
                                type="tel"
                                inputProps={phoneInputProps}
                                value={phoneNumber}
                                error={showPhoneNumberError}
                                helperText={showPhoneNumberError && t(phoneNumberValidationError?.key)}
                                onChange={(e) => setPhoneNumber(normalizePhoneNumber(e.target.value))}
                                onBlur={() => setPhoneNumberTouched(true)}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <FieldHelpSuffix
                                            asAdornment
                                            title={t('userData.help.phoneNumber')}
                                            ariaLabel={t('userData.help.phoneNumber') as string}
                                        />
                                    ),
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {cropDialogOpen && (
                <AvatarCropDialog
                    open={cropDialogOpen}
                    imageFile={selectedFile}
                    onClose={handleCropClose}
                    onSave={handleCropSave}
                />
            )}

            <ButtonGroup>
                <ActionButton
                    type="submit"
                    disabled={(!isFormChanged && !isAvatarChanged) || showPhoneNumberError}
                >
                    {t('common.saveChanges')}
                </ActionButton>
            </ButtonGroup>
        </Box>
    );
};

export default UserData;
